import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Video, PhoneOff, Users, Copy, Loader2, Mic, MicOff, VideoOff } from 'lucide-react';

const VideoCallPage = () => {
  const { roomId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [roomName, setRoomName] = useState(roomId || '');
  const [inCall, setInCall] = useState(false);
  const [jitsiApi, setJitsiApi] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [jitsiReady, setJitsiReady] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);

  // Check if Jitsi API is loaded
  useEffect(() => {
    const checkJitsi = () => {
      if (window.JitsiMeetExternalAPI) {
        setJitsiReady(true);
        return true;
      }
      return false;
    };

    if (checkJitsi()) return;

    const interval = setInterval(() => {
      if (checkJitsi()) {
        clearInterval(interval);
      }
    }, 500);

    const timeout = setTimeout(() => {
      clearInterval(interval);
      if (!window.JitsiMeetExternalAPI) {
        toast.error('Video call service is unavailable. Please try again later.');
      }
    }, 10000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, []);

  const startCall = useCallback((room) => {
    if (!window.JitsiMeetExternalAPI) {
      toast.error('Video call service is not ready. Please wait...');
      return;
    }

    const container = document.getElementById('jitsi-container');
    if (!container) {
      toast.error('Video container not found');
      return;
    }

    setIsLoading(true);

    try {
      const domain = 'meet.jit.si';
      const options = {
        roomName: room,
        width: '100%',
        height: 600,
        parentNode: container,
        userInfo: {
          displayName: user?.name || 'Guest'
        },
        configOverwrite: {
          startWithAudioMuted: false,
          startWithVideoMuted: false,
          prejoinPageEnabled: true, // Keep prejoin for permissions
          disableDeepLinking: true,
          enableWelcomePage: false,
          enableClosePage: false,
          disableInviteFunctions: false,
          toolbarButtons: [
            'microphone',
            'camera',
            'desktop',
            'fullscreen',
            'fodeviceselection',
            'hangup',
            'chat',
            'raisehand',
            'videoquality',
            'filmstrip',
            'participants-pane',
            'tileview',
            'select-background',
            'settings',
            'shortcuts',
            'recording',
          ],
          notifications: [],
          hideConferenceSubject: false,
          hideConferenceTimer: false,
          subject: `Khalsa Sidak - ${room.replace('KhalsaSidak-', '').replace(/-/g, ' ')}`,
        },
        interfaceConfigOverwrite: {
          TOOLBAR_BUTTONS: [
            'microphone',
            'camera',
            'desktop',
            'fullscreen',
            'fodeviceselection',
            'hangup',
            'chat',
            'raisehand',
            'videoquality',
            'filmstrip',
            'participants-pane',
            'tileview',
            'settings',
          ],
          SHOW_JITSI_WATERMARK: false,
          SHOW_WATERMARK_FOR_GUESTS: false,
          DEFAULT_BACKGROUND: '#1a365d',
          BRAND_WATERMARK_LINK: '',
          TOOLBAR_ALWAYS_VISIBLE: true,
          MOBILE_APP_PROMO: false,
          DISABLE_JOIN_LEAVE_NOTIFICATIONS: false,
          FILM_STRIP_MAX_HEIGHT: 120,
          SHOW_CHROME_EXTENSION_BANNER: false,
        }
      };

      const api = new window.JitsiMeetExternalAPI(domain, options);
      
      api.addEventListener('videoConferenceJoined', () => {
        setIsLoading(false);
        setInCall(true);
        toast.success('Connected to video call!');
      });
      
      api.addEventListener('videoConferenceLeft', () => {
        setInCall(false);
        api.dispose();
        setJitsiApi(null);
      });

      api.addEventListener('readyToClose', () => {
        setInCall(false);
        api.dispose();
        setJitsiApi(null);
        navigate('/teachers');
      });

      // Track audio/video state
      api.addEventListener('audioMuteStatusChanged', (data) => {
        setIsMuted(data.muted);
      });

      api.addEventListener('videoMuteStatusChanged', (data) => {
        setIsVideoOff(data.muted);
      });

      setJitsiApi(api);
    } catch (error) {
      console.error('Error starting Jitsi call:', error);
      toast.error('Failed to start video call. Please try again.');
      setIsLoading(false);
    }
  }, [user, navigate]);

  // Auto-join room if roomId is in URL
  useEffect(() => {
    if (roomId && user && jitsiReady && !inCall && !jitsiApi) {
      const timer = setTimeout(() => {
        startCall(roomId);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [roomId, user, jitsiReady, inCall, jitsiApi, startCall]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (jitsiApi) {
        jitsiApi.dispose();
      }
    };
  }, [jitsiApi]);

  const generateRoomName = () => {
    const random = Math.random().toString(36).substring(2, 8);
    return `KhalsaSidak-${random}`;
  };

  const handleJoinCall = () => {
    if (!roomName.trim()) {
      toast.error('Please enter a room name');
      return;
    }
    navigate(`/video-call/${roomName}`);
  };

  const handleCreateRoom = () => {
    const newRoom = generateRoomName();
    setRoomName(newRoom);
    navigate(`/video-call/${newRoom}`);
  };

  const copyRoomLink = () => {
    const link = `${window.location.origin}/video-call/${roomName || roomId}`;
    navigator.clipboard.writeText(link);
    toast.success('Room link copied! Share with others to invite them.');
  };

  const endCall = () => {
    if (jitsiApi) {
      jitsiApi.executeCommand('hangup');
    }
    navigate('/teachers');
  };

  // Custom control handlers
  const toggleMute = () => {
    if (jitsiApi) {
      jitsiApi.executeCommand('toggleAudio');
    }
  };

  const toggleVideo = () => {
    if (jitsiApi) {
      jitsiApi.executeCommand('toggleVideo');
    }
  };

  const showJitsiContainer = roomId && jitsiReady;

  return (
    <div className="min-h-screen bg-gray-900 py-4 sm:py-8">
      <div className="max-w-6xl mx-auto px-4">
        {!showJitsiContainer ? (
          <div className="max-w-md mx-auto">
            <Card className="bg-gray-800 border-gray-700">
              <CardContent className="p-6 sm:p-8 text-center">
                <div className="w-20 h-20 bg-saffron-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Video className="w-10 h-10 text-white" />
                </div>
                <h1 className="text-2xl font-bold text-white mb-2">Video Call</h1>
                <p className="text-gray-400 mb-6">Join or create a video call with teachers and students</p>
                
                {!jitsiReady && (
                  <div className="flex items-center justify-center gap-2 text-gray-400 mb-4">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Loading video call service...</span>
                  </div>
                )}
                
                <div className="space-y-4">
                  <div>
                    <Input
                      placeholder="Enter room name or code"
                      value={roomName}
                      onChange={(e) => setRoomName(e.target.value)}
                      className="bg-gray-700 border-gray-600 text-white placeholder:text-gray-400"
                    />
                  </div>
                  
                  <Button 
                    onClick={handleJoinCall} 
                    className="w-full bg-saffron-500 hover:bg-saffron-600 text-white"
                    disabled={!jitsiReady}
                  >
                    <Video className="w-4 h-4 mr-2" />
                    Join Call
                  </Button>
                  
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-600"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-2 bg-gray-800 text-gray-400">or</span>
                    </div>
                  </div>
                  
                  <Button 
                    onClick={handleCreateRoom} 
                    variant="outline" 
                    className="w-full border-gray-600 text-white hover:bg-gray-700"
                    disabled={!jitsiReady}
                  >
                    <Users className="w-4 h-4 mr-2" />
                    Create New Room
                  </Button>
                </div>

                {/* Features List */}
                <div className="mt-8 pt-6 border-t border-gray-700">
                  <p className="text-gray-400 text-sm mb-4">Features included:</p>
                  <div className="grid grid-cols-2 gap-2 text-xs text-gray-500">
                    <div className="flex items-center gap-2">
                      <Mic className="w-3 h-3 text-saffron-500" />
                      <span>Audio Call</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Video className="w-3 h-3 text-saffron-500" />
                      <span>Video Call</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <MicOff className="w-3 h-3 text-saffron-500" />
                      <span>Mute/Unmute</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <VideoOff className="w-3 h-3 text-saffron-500" />
                      <span>Camera On/Off</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (
          <div>
            {/* Header Controls */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4">
                <h2 className="text-white font-semibold text-sm sm:text-base truncate max-w-xs sm:max-w-md">
                  Room: {roomId?.replace('KhalsaSidak-', '').replace(/-/g, ' ')}
                </h2>
                <Button size="sm" variant="outline" onClick={copyRoomLink} className="text-white border-gray-600 hover:bg-gray-700">
                  <Copy className="w-4 h-4 mr-1" />
                  Copy Link
                </Button>
              </div>
              <div className="flex items-center gap-2">
                {/* Custom Audio/Video Controls */}
                {inCall && (
                  <>
                    <Button
                      size="sm"
                      variant={isMuted ? "destructive" : "outline"}
                      onClick={toggleMute}
                      className={isMuted ? "" : "text-white border-gray-600 hover:bg-gray-700"}
                      title={isMuted ? "Unmute" : "Mute"}
                    >
                      {isMuted ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                    </Button>
                    <Button
                      size="sm"
                      variant={isVideoOff ? "destructive" : "outline"}
                      onClick={toggleVideo}
                      className={isVideoOff ? "" : "text-white border-gray-600 hover:bg-gray-700"}
                      title={isVideoOff ? "Turn Camera On" : "Turn Camera Off"}
                    >
                      {isVideoOff ? <VideoOff className="w-4 h-4" /> : <Video className="w-4 h-4" />}
                    </Button>
                  </>
                )}
                <Button onClick={endCall} variant="destructive" size="sm">
                  <PhoneOff className="w-4 h-4 mr-2" />
                  Leave
                </Button>
              </div>
            </div>
            
            {isLoading && (
              <div className="flex items-center justify-center gap-2 text-white mb-4">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Connecting to video call...</span>
              </div>
            )}
            
            {/* Jitsi Container */}
            <div id="jitsi-container" className="rounded-xl overflow-hidden bg-black min-h-[400px] sm:min-h-[600px]"></div>
            
            {/* Info Footer */}
            <div className="mt-4 text-center">
              <p className="text-gray-400 text-sm">
                Share the room link with others to invite them to the call
              </p>
              <div className="flex items-center justify-center gap-4 mt-2 text-xs text-gray-500">
                <span className="flex items-center gap-1">
                  <Mic className="w-3 h-3" /> Use toolbar to mute/unmute
                </span>
                <span className="flex items-center gap-1">
                  <Video className="w-3 h-3" /> Toggle camera on/off
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoCallPage;
